import { useState } from 'react';
import { Bell, BellOff, Clock, Loader2 } from 'lucide-react';

// Shared, VERTICAL-AGNOSTIC notification control. Renders the two person-level
// flags every vertical uses:
//   sms_notify        permanent on/off  ("I don't want texts at all")
//   sms_snoozed_until temporary pause   ("not this weekend") — self-expiring
// Labels are injected so the caller supplies its own translation source.
const pad = (n) => String(n).padStart(2, '0');

export const presetSnooze = (kind) => {
    const d = new Date();
    if (kind === '2h') { d.setHours(d.getHours() + 2); return d; }
    if (kind === '2d') { d.setDate(d.getDate() + 2); return d; }
    d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0);       // tomorrow morning
    return d;
};

export const fmtResume = (iso) => {
    try {
        const d = new Date(iso);
        const today = new Date();
        const sameDay = d.toDateString() === today.toDateString();
        const time = `${d.getHours()}:${pad(d.getMinutes())}`;
        return sameDay ? time : `${d.toLocaleDateString(undefined, { weekday: 'long' })} ${time}`;
    } catch { return iso; }
};

export default function SmsNotifyControl({ state, labels, onSave, testid = 'sms-notify' }) {
    const [busy, setBusy] = useState(false);
    const L = labels || {};
    const snoozedUntil = state?.sms_snoozed_until || null;
    const on = state?.sms_notify !== false;
    const hasPhone = !!state?.has_phone;

    const save = async (payload) => {
        setBusy(true);
        try { await onSave(payload); } finally { setBusy(false); }
    };

    return (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3.5" data-testid={testid}>
            <div className="flex items-start gap-2.5">
                <span className="mt-0.5 shrink-0">
                    {on && !snoozedUntil ? <Bell className="w-4 h-4 text-amber-300" /> : <BellOff className="w-4 h-4 text-slate-400" />}
                </span>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white">{L.title}</p>
                    <p className="text-xs text-slate-300 mt-0.5">
                        {hasPhone ? `${L.sendTo} ${state.phone_masked}` : L.noPhone}
                    </p>
                </div>
                <button type="button" disabled={busy || !hasPhone} onClick={() => save({ sms_notify: !on })}
                    data-testid={`${testid}-toggle`}
                    className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-40 ${on ? 'bg-emerald-600 text-white hover:bg-emerald-500' : 'bg-white/10 text-slate-200 hover:bg-white/20'}`}>
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (on ? L.smsOn : L.smsOff)}
                </button>
            </div>

            {on && hasPhone && (
                <div className="mt-3 pt-3 border-t border-white/10">
                    {snoozedUntil ? (
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-200" data-testid={`${testid}-paused`}>
                                <Clock className="w-3.5 h-3.5" />
                                {(L.pausedUntil || 'Paused until {{when}}').replace('{{when}}', fmtResume(snoozedUntil))}
                            </span>
                            <button type="button" disabled={busy} onClick={() => save({ clear_snooze: true })}
                                data-testid={`${testid}-resume`}
                                className="ml-auto rounded-full bg-white/10 hover:bg-white/20 px-3 py-1.5 text-xs font-semibold text-white transition-colors disabled:opacity-40">
                                {L.resumeNow}
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-slate-400 mr-1">{L.pauseTitle}</span>
                            {[['2h', L.pause2h], ['tomorrow', L.pauseTomorrow], ['2d', L.pause2d]].map(([kind, label]) => (
                                <button key={kind} type="button" disabled={busy}
                                    onClick={() => save({ snooze_until: presetSnooze(kind).toISOString() })}
                                    data-testid={`${testid}-pause-${kind}`}
                                    className="rounded-full border border-white/15 hover:border-amber-400/60 hover:text-amber-200 px-3 py-1.5 text-xs font-semibold text-slate-200 transition-colors disabled:opacity-40">
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
